import React, { useState, useRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Upload, Camera, ImageIcon, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import WebcamCapture from '../components/WebcamCapture'

const UploadMedicine = () => {
  const { API } = useContext(AuthContext)
  const navigate = useNavigate()
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [fileType, setFileType] = useState('image')
  const [dragOver, setDragOver] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [showWebcam, setShowWebcam] = useState(false)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const handleFileChange = (file) => {
    if (!file) return

    // Check if it's a PDF or image
    if (file.type === 'application/pdf') {
      setFileType('pdf')
      const reader = new FileReader()
      reader.onload = (e) => {
        setImage(e.target.result.split(',')[1])
        setImagePreview(null) // Don't show preview for PDF
        toast.success('PDF uploaded successfully! Click Process to extract data.')
      }
      reader.readAsDataURL(file)
      return
    }

    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      toast.error('Please upload a valid file (JPEG, JPG, PNG, or PDF)')
      return
    }

    setFileType('image')
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target.result)
      setImage(e.target.result.split(',')[1]) // Get base64 without prefix
    }
    reader.readAsDataURL(file)
  }

  const handleWebcamCapture = (base64Data, previewUrl) => {
    setImage(base64Data)
    setImagePreview(previewUrl)
    setFileType('image')
    toast.success('Photo captured successfully!')
  }

  const handleCameraClick = () => {
    // Detect if on mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    if (isMobile) {
      // On mobile, use native camera
      cameraInputRef.current?.click()
    } else {
      // On desktop, use WebRTC webcam
      setShowWebcam(true)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    handleFileChange(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleProcessOCR = async () => {
    if (!image) {
      toast.error('Please upload a file first')
      return
    }

    setProcessing(true)

    try {
      const response = await axios.post(`${API}/ocr/process`, { 
        image_base64: image,
        file_type: fileType
      })
      
      if (!response.data.medicines || response.data.medicines.length === 0) {
        toast.error('No medicines found in the file. Please ensure the image is clear and contains medicine information.')
        setProcessing(false)
        return
      }

      // Use display image for viewing
      const displayImage = response.data.display_image_base64 || image
      
      // Store in sessionStorage as backup for React Router v7 state issues
      const navigationData = { 
        medicines: response.data.medicines, 
        imageBase64: displayImage, 
        fileType 
      }
      sessionStorage.setItem('ocrNavigationData', JSON.stringify(navigationData))
      
      navigate('/verify', { state: navigationData })
      toast.success(`Extracted ${response.data.medicines.length} medicines successfully!`)
    } catch (error) {
      console.error('OCR error:', error)
      toast.error(error.response?.data?.detail || 'OCR processing failed. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Layout title="Upload Medicine">
      <div className="card" data-testid="upload-card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Delivery Challan</h2>
        <p className="text-gray-600 mb-6">Upload a delivery challan image (PNG, JPG, PDF) for OCR processing, or use your camera to capture it directly</p>

        <div className="flex gap-4 mb-6 flex-wrap">
          <Button
            variant="outline"
            onClick={handleCameraClick}
            data-testid="camera-button"
          >
            <Camera className="w-4 h-4 mr-2" />
            Use Camera
          </Button>
          <span className="text-gray-500 flex items-center">or</span>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            data-testid="browse-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Browse Files
          </Button>
        </div>

        {/* WebRTC Webcam Modal */}
        {showWebcam && (
          <WebcamCapture
            onCapture={handleWebcamCapture}
            onClose={() => setShowWebcam(false)}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,application/pdf"
          onChange={(e) => handleFileChange(e.target.files[0])}
          className="hidden"
          data-testid="file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFileChange(e.target.files[0])}
          className="hidden"
          data-testid="camera-input"
        />

        <div
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="drop-zone"
        >
          {imagePreview ? (
            <div className="space-y-4">
              <img src={imagePreview} alt="Preview" className="max-h-96 mx-auto rounded-lg shadow" data-testid="image-preview" />
              <p className="text-gray-600">Image uploaded successfully!</p>
            </div>
          ) : image && fileType === 'pdf' ? (
            <div className="space-y-4">
              <div className="w-24 h-24 mx-auto bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" />
                </svg>
              </div>
              <p className="text-gray-600">PDF uploaded successfully!</p>
            </div>
          ) : (
            <>
              <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Or drag and drop your file here</p>
              <p className="text-sm text-gray-500">PNG, JPG, or PDF (MAX. 10MB)</p>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleProcessOCR}
            disabled={!image || processing}
            data-testid="process-ocr-button"
            className="min-w-[200px]"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing OCR...
              </>
            ) : (
              'Process with OCR'
            )}
          </Button>
        </div>
      </div>
    </Layout>
  )
}

export default UploadMedicine